import React, { useState, useEffect } from 'react'
import { message } from 'antd'
import { useNavigate } from 'react-router-dom'
import { createBanner, uploadImage } from '../../../Web Apis/webApis'
import Load from '../../Load/Load'
import { FaArrowLeft, FaUpload, FaTrash } from 'react-icons/fa'

const AddBannerPage = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadedImageUrl, setUploadedImageUrl] = useState("")
    const [selectedFile, setSelectedFile] = useState(null)
    const [imagePreview, setImagePreview] = useState("")

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'promotional',
        linkUrl: '',
        isActive: true,
        sortOrder: 0,
        buttonText: '',
        altText: ''
    })

    const bannerTypes = [
        { value: 'promotional', label: 'Promotional' },
        { value: 'informational', label: 'Informational' },
        { value: 'announcement', label: 'Announcement' },
        { value: 'featured', label: 'Featured' }
    ]

    const handleImageUpload = async (event) => {
        const file = event.target.files[0]
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
            if (!allowedTypes.includes(file.type)) {
                message.error('Please select a valid image file (JPEG, PNG, GIF, WebP)')
                return
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                message.error('Image size should not exceed 5MB')
                return
            }

            try {
                setUploading(true)
                setSelectedFile(file)

                // Upload image to server
                const uploadResponse = await uploadImage(file)
                if (uploadResponse?.data?.success) {
                    const imageUrl = uploadResponse.data.imageUrl || uploadResponse.data.url
                    setUploadedImageUrl(imageUrl)
                    message.success('Image uploaded successfully')
                } else {
                    throw new Error('Upload failed')
                }

                // Create preview URL for display
                const reader = new FileReader()
                reader.onload = (e) => {
                    setImagePreview(e.target.result)
                }
                reader.readAsDataURL(file)

            } catch (error) {
                console.error('Error uploading image:', error)
                message.error(error?.response?.data?.message || 'Failed to upload image')
            } finally {
                setUploading(false)
            }
        }
    }

    const removeImage = () => {
        setImagePreview("")
        setUploadedImageUrl("")
        setSelectedFile(null)
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Validate required fields
        if (!uploadedImageUrl) {
            message.error("Please upload an image for the banner")
            return
        }

        if (!formData.title.trim()) {
            message.error("Please enter a title for the banner")
            return
        }

        if (!formData.type) {
            message.error("Please select a banner type")
            return
        }

        try {
            setLoading(true)

            // Create FormData for multipart upload
            const bannerData = new FormData()
            bannerData.append('banner_image', selectedFile)
            bannerData.append('title', formData.title)
            bannerData.append('description', formData.description)
            bannerData.append('type', formData.type)
            bannerData.append('linkUrl', formData.linkUrl)
            bannerData.append('isActive', formData.isActive)
            bannerData.append('sortOrder', parseInt(formData.sortOrder))
            bannerData.append('buttonText', formData.buttonText)
            bannerData.append('altText', formData.altText)

            const response = await createBanner(bannerData)
            if (response?.data?.success) {
                message.success(response?.data?.message || "Banner created successfully")
                navigate('/admin/banners')
            }

        } catch (error) {
            console.log("Error while creating banner", error.message)
            message.error(error?.response?.data?.message || "Error while creating banner")
        } finally {
            setLoading(false)
        }
    }

    const handleGoBack = () => {
        navigate('/admin/banners')
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleGoBack}
                            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-200"
                        >
                            <FaArrowLeft className="mr-2" />
                            Back to Banners
                        </button>
                        <h1 className="text-3xl font-bold text-gray-800">Add New Banner</h1>
                    </div>
                </div>

                {/* Main Form */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Image Upload Section */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Banner Image <span className="text-red-500">*</span>
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                {!imagePreview ? (
                                    <div>
                                        <FaUpload className="mx-auto text-gray-400 text-3xl mb-4" />
                                        <p className="text-gray-600 mb-4">
                                            Click to upload or drag and drop
                                        </p>
                                        <label className="cursor-pointer">
                                            <span className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200 inline-block">
                                                Choose Image
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                className="hidden"
                                                disabled={uploading}
                                            />
                                        </label>
                                        <p className="text-sm text-gray-500 mt-2">
                                            Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB
                                        </p>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <img
                                            src={imagePreview}
                                            alt="Banner preview"
                                            className="max-w-full max-h-64 object-contain mx-auto rounded-md"
                                        />
                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition duration-200"
                                            title="Remove image"
                                        >
                                            <FaTrash className="text-sm" />
                                        </button>
                                        {uploading && (
                                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-md">
                                                <div className="text-white">Uploading...</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Title */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter banner title"
                                    required
                                />
                            </div>

                            {/* Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="type"
                                    value={formData.type}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                >
                                    {bannerTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Link URL */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Link URL
                                </label>
                                <input
                                    type="url"
                                    name="linkUrl"
                                    value={formData.linkUrl}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="https://example.com"
                                />
                            </div>

                            {/* Sort Order */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Sort Order
                                </label>
                                <input
                                    type="number"
                                    name="sortOrder"
                                    value={formData.sortOrder}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="0"
                                    min="0"
                                />
                            </div>

                            {/* Button Text */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Button Text
                                </label>
                                <input
                                    type="text"
                                    name="buttonText"
                                    value={formData.buttonText}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="e.g., Learn More"
                                />
                            </div>

                            {/* Alt Text */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Alt Text
                                </label>
                                <input
                                    type="text"
                                    name="altText"
                                    value={formData.altText}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Image alt text for accessibility"
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows={4}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter banner description"
                            />
                        </div>

                        {/* Active Checkbox */}
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                name="isActive"
                                id="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                                className="mr-2"
                            />
                            <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                                Active (Banner will be displayed)
                            </label>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t">
                            <button
                                type="button"
                                onClick={handleGoBack}
                                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || uploading || !uploadedImageUrl}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? "Creating..." : "Create Banner"}   ---------
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {(loading || uploading) && <Load />}
        </div>
    )
}

export default AddBannerPage

